import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle, Lightning, Lock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Waitlist landing page — public route at /waitlist.
 *
 * What it does:
 *  - Pre-launch lead capture for Jimmy's Instagram traffic.
 *  - Captures: Name, Email, Mobile, Weight-loss goal (+ optional source from `?src=` query string).
 *  - POSTs to /api/waitlist which stores the lead and emails Jimmy so he can SMS/call
 *    inside the first hour.
 *  - Success state thanks them and sets expectation that Jimmy will reach out personally.
 *
 * Designed standalone — doesn't depend on AuthContext / MembershipContext so it can be linked
 * from IG bio without users needing to log in or even know the app exists yet.
 */
export default function WaitlistPage() {
  const [params] = useSearchParams();
  const presetSource = params.get("src") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [goal, setGoal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "The Lazy Lean Method — Applications opening soon";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Drop your name.");
    if (!email.trim() || !email.includes("@")) return setErr("Need a valid email.");
    if (mobile.replace(/[^0-9]/g, "").length < 6) return setErr("Need a valid mobile so Jimmy can reach you.");
    if (!goal.trim()) return setErr("Tell Jimmy your weight-loss goal.");

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        mobile: mobile.trim(),
        weight_loss_goal: goal.trim(),
        source: presetSource || "instagram",
      });
      setSuccess(true);
      setDuplicate(!!data?.duplicate);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-manrope">
      {/* Top brand bar */}
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/waitlist" className="flex items-center gap-2.5" data-testid="waitlist-brand">
            <div className="w-7 h-7 bg-[#FF3333] flex items-center justify-center">
              <Lightning size={16} weight="fill" className="text-black" />
            </div>
            <span className="font-oswald text-base uppercase tracking-[0.2em] text-white">Lazy Lean Method</span>
          </Link>
          <div className="text-[10px] sm:text-xs font-oswald uppercase tracking-[0.25em] text-zinc-500">
            Waitlist
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — pitch */}
          <div className="lg:col-span-7">
            <div className="id-label text-[#FF3333] mb-4">— By application only —</div>
            <h1 className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6 uppercase tracking-tight text-white">
              Applications for <span className="text-[#FF3333]">The Lazy Lean Method</span> are opening soon.
            </h1>
            <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed mb-6 max-w-xl">
              This isn't another fitness app. It's the system I built for men who are done starting over every Monday — and once the doors open, they don't stay open long.
            </p>
            <p className="text-base text-zinc-400 leading-relaxed mb-8 max-w-xl">
              I'm taking on a small, hand-picked first wave. When the spots are gone, that's it — the next intake won't open for months. Get on the list now, and you'll hear from me <span className="text-white font-semibold">before everyone else</span>.
            </p>

            <div className="space-y-3 mb-10 max-w-xl">
              {[
                "Built for men who hate diets",
                "Coached personally — not a chatbot, not an app you forget about",
                "Designed around your life, your schedule, your gym",
                "A simple system you can actually keep doing past week 3",
                "A brotherhood that won't let you ghost yourself",
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`waitlist-bullet-${i}`}>
                  <span className="text-[#FF3333] text-base font-bold leading-none pt-1.5 w-3 shrink-0">→</span>
                  <span className="text-base text-zinc-200 leading-snug">{line}</span>
                </div>
              ))}
            </div>

            <div className="border-l-2 border-[#FF3333] pl-4 mb-10 max-w-xl">
              <p className="text-sm text-zinc-300 italic leading-relaxed">
                "If you're in the position you were in 12 months ago, you can't afford to wait another 12. The blokes I work with don't wait — they act while it's uncomfortable."
              </p>
              <p className="text-xs text-zinc-500 mt-2 font-oswald uppercase tracking-[0.2em]">— Jimmy</p>
            </div>

            <div className="flex items-center gap-3 text-xs font-oswald uppercase tracking-[0.18em] text-zinc-500">
              <Lock size={14} weight="bold" />
              <span>Your details go to Jimmy directly. Never shared.</span>
            </div>
          </div>

          {/* Right — form / success */}
          <div className="lg:col-span-5">
            <div className="bg-[#0A0A0A] border border-[#262626] p-6 sm:p-8 sticky top-8">
              {success ? (
                <div data-testid="waitlist-success" className="text-center py-6">
                  <CheckCircle size={56} weight="fill" className="text-[#FF3333] mx-auto mb-5" />
                  <h2 className="font-oswald text-2xl sm:text-3xl font-bold uppercase tracking-wide text-white mb-3 leading-tight">
                    {duplicate ? "You're already in." : "You're on the list."}
                  </h2>
                  <p className="text-zinc-300 text-base leading-relaxed mb-6">
                    {duplicate
                      ? "I've still got your details. You'll be one of the first I contact when applications open."
                      : "When applications open, you'll be one of the first I personally reach out to. Watch your phone and inbox over the coming weeks."}
                  </p>
                  <div className="border-t border-[#262626] pt-6">
                    <p className="text-xs font-oswald uppercase tracking-[0.2em] text-zinc-500 mb-3">In the meantime</p>
                    <a
                      href="https://www.instagram.com/lazyleanmethod"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#FF3333] hover:text-white transition-colors font-oswald uppercase tracking-wide text-sm"
                      data-testid="waitlist-follow-ig"
                    >
                      Follow on Instagram <ArrowRight size={14} weight="bold" />
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="waitlist-form">
                  <div className="id-label text-[#FF3333] mb-2">— Register interest —</div>
                  <h2 className="font-oswald text-xl sm:text-2xl font-bold uppercase tracking-wide text-white leading-tight mb-1">
                    Get in while you can
                  </h2>
                  <p className="text-zinc-500 text-sm mb-5">Takes 30 seconds. No spam ever. You'll be one of the first contacted.</p>

                  <div>
                    <label className="id-label block mb-2" htmlFor="wl-name">Name</label>
                    <input
                      id="wl-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="id-input w-full"
                      placeholder="Your name"
                      autoComplete="name"
                      data-testid="waitlist-name"
                      required
                    />
                  </div>

                  <div>
                    <label className="id-label block mb-2" htmlFor="wl-email">Email</label>
                    <input
                      id="wl-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="id-input w-full"
                      placeholder="you@example.com"
                      autoComplete="email"
                      data-testid="waitlist-email"
                      required
                    />
                  </div>

                  <div>
                    <label className="id-label block mb-2" htmlFor="wl-mobile">Mobile</label>
                    <input
                      id="wl-mobile"
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      className="id-input w-full"
                      placeholder="04XX XXX XXX"
                      autoComplete="tel"
                      data-testid="waitlist-mobile"
                      required
                    />
                    <div className="text-xs text-zinc-600 mt-1">So Jimmy can text or call you personally.</div>
                  </div>

                  <div>
                    <label className="id-label block mb-2" htmlFor="wl-goal">Weight-loss goal</label>
                    <textarea
                      id="wl-goal"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      rows={3}
                      className="id-input w-full resize-none"
                      placeholder="e.g. I'm 95kg and want to get to 85kg in the next 6 months."
                      maxLength={500}
                      data-testid="waitlist-goal"
                      required
                    />
                    <div className="text-xs text-zinc-600 mt-1 text-right">{goal.length}/500</div>
                  </div>

                  {err && (
                    <div className="text-sm text-[#FF3333] border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2" data-testid="waitlist-error">
                      {err}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="id-btn-primary w-full text-base mt-2"
                    data-testid="waitlist-submit"
                  >
                    {submitting ? "Sending..." : (<>Register your interest <ArrowRight size={16} weight="bold" /></>)}
                  </button>

                  <p className="text-xs text-zinc-600 text-center pt-2">
                    By submitting, you agree Jimmy can contact you about the program. No marketing emails ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-20 border-t border-[#1A1A1A] pt-8 text-center">
          <p className="text-xs text-zinc-600 font-manrope max-w-2xl mx-auto">
            The Lazy Lean Method™ — For men who hate diets. Applications open in waves. Spots are intentionally limited.
          </p>
        </div>
      </main>
    </div>
  );
}
